import { useState, useEffect, Dispatch, SetStateAction } from "react";
import { findWhere } from "underscore";
import { Collapse } from "reactstrap";
import { useAtomValue } from "jotai";

import SelectableReferences from "./ReferencesComponents/SelectableReferences/SelectableReferences";
import ReferenceSearch, {
  getReferenceQuery,
} from "./ReferencesComponents/ReferenceSearch";
import ViewRecordDrawer from "../../../components/Drawers/Records/ViewRecordDrawer";
import ReferenceModuleSelect from "./ReferencesComponents/ReferenceModuleSelect";
import SelectedSubitemHeader from "./ReferencesComponents/SelectedSubitemHeader";
import MetadataFieldSettings from "./ReferencesComponents/MetadataFieldSettings";
import SubitemDrawer from "../../../components/Drawers/Subitems/SubitemDrawer";
import { ReusableViewReferenceFields } from "../../../types/PageInterfaces";
import SelectedReferences from "./ReferencesComponents/SelectedReferences";
import useAvailableModules from "./ReferencesHooks/useAvailableModules";
import ViewRecordButton from "./ReferencesComponents/ViewRecordButton";
import ReferenceHeader from "./ReferencesComponents/ReferenceHeader";
import CollapseHeader from "./ReferencesComponents/CollapseHeader";
import useView from "../../../api/api-hooks/views/useView";
import AssignSelf from "./ReferencesComponents/AssignSelf";
import AddRecord from "./ReferencesComponents/AddRecord";
import { modulesAtom } from "../../../atoms/GlobalAtoms";
import { Module } from "../../../types/TenantInterfaces";
import FieldUtils from "../../../utils/FieldUtils";
import useModule from "../../../hooks/useModule";

const getModuleName = (value: any) => {
  let moduleAPIName = value?._ModuleAPIName;

  if (value?._isSubitem) {
    moduleAPIName = moduleAPIName + "." + value?._SubitemFieldAPIName;
  }

  return moduleAPIName;
};

const getBaseModule = (
  values: Array<any> | undefined,
  modulesMap: Map<string, any>,
  setCurrentModule: Dispatch<SetStateAction<any>>,
  setCurrentKey: Dispatch<SetStateAction<string>>,
  defaultModule: Module
) => {
  let modAPIName;

  if (!values || values?.length === 0) {
    modAPIName = modulesMap.keys().next().value;
  } else if (Array.isArray(values)) {
    modAPIName = getModuleName(values[0]);
  } else {
    modAPIName = getModuleName(values);
  }

  const mod = defaultModule || modulesMap.get(modAPIName)?.Module;

  setCurrentModule(mod);
  setCurrentKey(defaultModule?.APIName || modAPIName);
};

interface ReferenceProps {
  field: any;
  defaultOpen?: boolean;
  oneTap: boolean;
  selected: any;
  setSelected: Dispatch<SetStateAction<any>>;
  onClose: () => void;
  setKeepOpen: Dispatch<SetStateAction<boolean>>;
  setHideReference: Dispatch<SetStateAction<boolean>>;
  reusableViewReferenceFields?: ReusableViewReferenceFields;
  pageID: string;
  searchCustomField?: string;
  showFieldSettings?: boolean;
  setShowFieldSettings: Dispatch<SetStateAction<boolean>> | null;
  disableAddRecord?: boolean;
  showDisplayButton?: boolean;
}

const Reference = ({
  field,
  defaultOpen = false,
  oneTap,
  selected,
  setSelected,
  onClose,
  setKeepOpen,
  setHideReference,
  reusableViewReferenceFields,
  pageID,
  searchCustomField,
  showFieldSettings,
  setShowFieldSettings,
  disableAddRecord,
  showDisplayButton,
}: ReferenceProps) => {
  const { modulesMap, loading } = useAvailableModules(field?.SourcesList);
  const { getModule } = useModule();
  const modules = useAtomValue(modulesAtom);

  const [selectRefsOpen, setSelectRefsOpen] = useState(defaultOpen);
  const [currentKey, setCurrentKey] = useState("");
  const [currentModule, setCurrentModule] = useState<Module | null>(null);
  const [createdRecords, setCreatedRecords] = useState([]);
  const [viewRecord, setViewRecord] = useState<any>(null);
  const [query, setQuery] = useState(getReferenceQuery());
  const [selectedSubitemParent, setSelectedSubitemParent] = useState(null);
  const isSubitemRef = currentKey?.split(".")?.length > 1;

  // Disable some features when we are using this component in a public form.
  const isPublicForm = window.location.pathname.startsWith("/forms");
  const showAddRecord = !disableAddRecord && !isPublicForm;
  const showSelectedReferences = !isPublicForm;

  const sourceStr = Array.isArray(field.SourcesList)
    ? field.SourcesList[0]
    : typeof field.SourcesList === "string"
    ? field.SourcesList
    : null;

  const cloudID = !["Tenant", "Modules", "Settings"].includes(
    sourceStr?.split(".")[0]
  )
    ? sourceStr?.split(".")[0] !== "Any"
      ? sourceStr?.split(".")[0]
      : undefined
    : undefined;

  const fieldAPIName = field?.SubitemParent
    ? `${field?.SubitemParent}.${field?.APIName}`
    : field?.APIName;
  const { view } = useView({
    pageID,
    viewID:
      reusableViewReferenceFields?.[fieldAPIName]?.[
        viewRecord?.Module?.APIName
      ],
    disabled:
      !reusableViewReferenceFields?.[fieldAPIName]?.[
        viewRecord?.Module?.APIName
      ] || !!viewRecord?.isSubitem,
  });

  useEffect(() => {
    if (loading || !modules?.length || !field?.SourcesList || currentModule)
      return;
    const sourceList =
      field?.DefaultSource || field?.SourcesList?.[0] || field?.SourcesList;

    const defaultModule = FieldUtils.getSourceModuleForField(
      modules,
      sourceList
    );

    getBaseModule(
      selected,
      modulesMap,
      setCurrentModule,
      setCurrentKey,
      defaultModule
    );
  }, [loading, modules, modulesMap, selected, currentModule, field]);

  if (!loading && modulesMap?.size === 0) {
    return (
      <div
        style={{
          minWidth: "280px",
          maxWidth: "380px",
          width: "90vw",
        }}
      >
        <ReferenceHeader name={field?.Name} onClose={onClose} />
        <div className="text-danger p-4 font-weight-500 text-center">
          Sorry, but an error occurred.
          <br /> The Data Source(s) you are referencing were not found. Check
          your field settings or contact support.
        </div>
      </div>
    );
  }

  return (
    <>
      {showFieldSettings ? (
        <MetadataFieldSettings
          module={currentModule!}
          onClose={() => {
            setShowFieldSettings && setShowFieldSettings(false);
          }}
        />
      ) : (
        <div
          style={{
            minWidth: "280px",
            maxWidth: "380px",
            width: "90vw",
          }}
        >
          {field?.Name && (
            <>
              <ReferenceHeader name={field?.Name} onClose={onClose} />
              {showSelectedReferences && (
                <div
                  style={{
                    maxHeight: 160,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  <SelectedReferences
                    field={field}
                    selected={selected}
                    setSelected={setSelected}
                    onView={onViewRecord}
                    setKeepOpen={setKeepOpen}
                    modulesMap={modulesMap}
                  />
                </div>
              )}
              <CollapseHeader
                title={`Select ${field?.Name}`}
                onClick={() => setSelectRefsOpen((prev) => !prev)}
                isOpen={selectRefsOpen}
              />
            </>
          )}
          <Collapse isOpen={selectRefsOpen}>
            <div className="d-flex px-2 pt-2">
              {selectedSubitemParent && (
                <>
                  <SelectedSubitemHeader
                    selectedSubitemParent={selectedSubitemParent}
                    setSelectedSubitemParent={setSelectedSubitemParent}
                  />
                </>
              )}
              {!selectedSubitemParent && (
                <ReferenceModuleSelect
                  currentModule={currentModule}
                  currentKey={currentKey}
                  onSwitchModule={setCurrentModule}
                  onSwitchKey={setCurrentKey}
                  modulesMap={modulesMap}
                  disabled={!currentModule}
                  setKeepOpen={setKeepOpen}
                />
              )}
              {showAddRecord &&
                !isSubitemRef &&
                currentModule?.APIName === "Contacts" && (
                  <AssignSelf
                    currentModule={currentModule}
                    field={field}
                    selected={selected}
                    setSelected={setSelected}
                    oneTap={oneTap}
                    onClose={onClose}
                    setKeepOpen={setKeepOpen}
                  />
                )}
              {selectedSubitemParent && (
                <ViewRecordButton
                  onClick={() => onViewRecord(selectedSubitemParent)}
                />
              )}
              {showAddRecord && (
                <AddRecord
                  currentModule={currentModule}
                  setKeepOpen={setKeepOpen}
                  setHideReference={setHideReference}
                  setCreatedRecords={setCreatedRecords}
                  selectedSubitemParent={selectedSubitemParent}
                  setSelectedSubitemParent={setSelectedSubitemParent}
                  modulesMap={modulesMap}
                  currentKey={currentKey}
                />
              )}
            </div>
            <hr className="my-2" />
            <div className="px-2">
              <ReferenceSearch
                searchCustomField={searchCustomField}
                setQuery={setQuery}
              />
            </div>
            <hr className="my-2" />
            <SelectableReferences
              currentModule={currentModule!}
              currentKey={currentKey}
              modulesMap={modulesMap}
              field={field}
              query={query}
              setQuery={setQuery}
              selected={selected}
              setSelected={setSelected}
              setKeepOpen={setKeepOpen}
              setHideReference={setHideReference}
              oneTap={oneTap}
              onClose={onClose}
              createdRecords={createdRecords}
              selectedSubitemParent={selectedSubitemParent}
              setSelectedSubitemParent={setSelectedSubitemParent}
              setShowFieldSettings={setShowFieldSettings}
              tenantID={cloudID}
              showDisplayButton={showDisplayButton}
            />
          </Collapse>
        </div>
      )}
      <ViewRecordDrawer
        open={!!viewRecord && !viewRecord?.isSubitem}
        onClose={onCloseViewRecord}
        recordID={viewRecord?.RecordID}
        module={viewRecord?.Module}
        view={view || undefined}
        fromReusableViewReference={!!view}
      />
      <SubitemDrawer
        open={!!viewRecord && viewRecord?.isSubitem}
        recordID={viewRecord?.RecordID}
        subitemID={viewRecord?.SubitemID}
        moduleAPIName={viewRecord?.Module?.APIName}
        field={viewRecord?.SubitemSchema}
        onClose={onCloseViewRecord}
        displayMode={"SummaryCard"}
        reusableViewReferenceFields={reusableViewReferenceFields}
      />
    </>
  );

  function onCloseViewRecord() {
    setHideReference(false);
    setKeepOpen(false);
    setViewRecord(null);
  }

  function onViewRecord(record: any) {
    setHideReference(true);
    setKeepOpen(true);

    if (record?._isSubitem) {
      const module = getModule(record?._ModuleAPIName);

      if (!module) throw new Error("Module not found");

      const foundField = findWhere(module?.Fields, {
        APIName: record?._SubitemFieldAPIName,
      });

      return setViewRecord({
        isSubitem: true,
        RecordID: record?.id,
        Module: module,
        SubitemSchema: foundField,
        SubitemID: record?._SubitemID,
      });
    }

    setViewRecord({
      RecordID: record?.id,
      Module: getModule(record?._ModuleAPIName),
    });
  }
};

export default Reference;
